/**
 * 设定详情组件
 *
 * 展示选中的设定对象的完整信息，包括Markdown格式的内容和作用范围说明
 * 支持编辑和删除设定
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { Setting, SettingLevel, SettingType } from '../../types/setting';
import { SettingService } from '../../services/setting';
import { useAuthStore } from '../../store/authStore';
import { useAutoTrackStore } from '../../store/autoTrackStore';
import { useTextSelectionStore } from '../../store/textSelectionStore';
import { KnowledgeGraphModal } from '../knowledge/KnowledgeGraphModal';

// SVG图标常量
const EDIT_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const DELETE_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const GRAPH_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 002-2h2a2 2 0 002 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const SAVE_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const CANCEL_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const EMPTY_ICON = (
  <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const ERROR_ICON = (
  <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const LOADING_SPINNER = (
  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />
);

// 辅助函数
const getLevelLabel = (level: SettingLevel): string => {
  switch (level) {
    case 'global':
      return '全文级';
    case 'volume':
      return '卷级';
    case 'chapter':
      return '章节级';
    default:
      return level;
  }
};

const getTypeLabel = (type: SettingType): string => {
  const labels: Record<string, string> = {
    charter: '宪章',
    outline: '大纲',
    detail: '细纲',
    summary: '摘要',
    character: '人物',
    supporting: '配角',
    location: '地点',
    item: '物品'
  };
  return labels[type] || type;
};

const formatScope = (level: SettingLevel, scope: string | null): string => {
  if (level === 'global') {
    return '整个工作区';
  }
  if (level === 'chapter') {
    return scope ? `第${scope}章` : '-';
  }
  if (level === 'volume') {
    return scope ? `章节${scope}` : '-';
  }
  return scope || '-';
};

const formatDateTime = (isoString: string): string => {
  return new Date(isoString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const sanitizeErrorMessage = (_error: unknown): string => {
  return '操作失败，请重试';
};

// 常量定义
const MAX_CONTENT_SIZE = 10 * 1024; // 10KB

// 组件Props接口
interface SettingDetailProps {
  /** 当前选中的设定对象，如果为null则显示空状态 */
  setting: Setting | null;
  /** 工作区ID */
  workspaceId: number;
  /** 设定更新成功后的回调，支持 null 参数用于设定被删除时清空选中状态 */
  onUpdate?: (setting: Setting | null) => void;
  /** 设定删除成功后的回调 */
  onDelete?: (settingId: number) => void;
  /** 设定服务实例(可选) */
  settingService?: SettingService;
}

export const SettingDetail: React.FC<SettingDetailProps> = ({
  setting,
  workspaceId,
  onUpdate,
  onDelete,
  settingService: externalSettingService
}) => {
  // 使用ref跟踪组件是否已挂载，防止组件卸载后的状态更新
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // State定义
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGraphModal, setShowGraphModal] = useState(false);

  // 高亮效果状态
  const [showHighlight, setShowHighlight] = useState(false);
  const highlightInfo = useAutoTrackStore((state) => state.highlightInfo);
  const clearHighlight = useAutoTrackStore((state) => state.clearHighlight);

  // 文字选中弹窗状态
  const showPopup = useTextSelectionStore((state) => state.showPopup);
  const hidePopup = useTextSelectionStore((state) => state.hidePopup);

  // 获取 token
  const { token } = useAuthStore();

  const [settingService] = useState<SettingService>(
    () => externalSettingService || new SettingService(undefined, token ?? undefined, workspaceId)
  );

  // 监听 setting prop 变化，重置编辑状态
  useEffect(() => {
    if (setting) {
      setIsEditing(false);
      setEditingContent('');
      setError(null);
    }
  }, [setting?.id, setting?.content]);

  // 监听高亮效果
  useEffect(() => {
    // 检查是否有高亮信息且匹配当前设定
    if (highlightInfo &&
        highlightInfo.targetType === 'setting' &&
        setting &&
        highlightInfo.targetId === setting.id) {
      // 触发高亮动画
      setShowHighlight(true);

      // 定时清除高亮效果
      const timer = setTimeout(() => {
        setShowHighlight(false);
        clearHighlight();
      }, highlightInfo.duration);

      return () => clearTimeout(timer);
    }
  }, [highlightInfo, setting?.id, clearHighlight]);

  // 处理编辑按钮点击
  const handleEdit = useCallback(() => {
    if (!setting) return;
    // 直接使用 Markdown 字符串
    setEditingContent(setting.content);
    setIsEditing(true);
    setError(null);
  }, [setting]);

  // 处理保存操作
  const handleSave = useCallback(async () => {
    if (!setting) return;

    // 验证内容大小
    if (editingContent.length > MAX_CONTENT_SIZE) {
      setError(`内容过大，最大允许 ${MAX_CONTENT_SIZE / 1024}KB`);
      return;
    }

    // 验证内容非空
    if (!editingContent.trim()) {
      setError('设定内容不能为空');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 直接发送 Markdown 字符串，使用服务器返回的数据
      const updatedSetting = await settingService.updateSetting(workspaceId, setting.id, {
        content: editingContent
      });

      if (!isMountedRef.current) return;

      onUpdate?.(updatedSetting);
      setIsEditing(false);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[SettingDetail] 保存失败:', err);
      setError(sanitizeErrorMessage(err));
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [setting, workspaceId, editingContent, settingService, onUpdate]);

  // 处理取消编辑
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingContent('');
    setError(null);
  }, []);

  // 处理删除按钮点击
  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  // 确认删除
  const handleConfirmDelete = useCallback(async () => {
    if (!setting) return;

    setLoading(true);
    setError(null);

    try {
      await settingService.deleteSetting(workspaceId, setting.id);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      setShowDeleteConfirm(false);
      onDelete?.(setting.id);
    } catch (err) {
      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      console.error('[SettingDetail] 删除失败:', err);
      setError(sanitizeErrorMessage(err));
    } finally {
      // 检查组件是否仍然挂载
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [setting, workspaceId, settingService, onDelete]);

  // 取消删除
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // ==================== 文字选中处理 ====================

  // 获取 textarea 中指定字符位置的精确坐标（从 ChapterEditor 复用）
  const getCaretCoordinates = useCallback((textarea: HTMLTextAreaElement, position: number): { top: number; left: number; height: number } => {
    const mirror = document.createElement('div');
    mirror.id = 'textarea-caret-position-mirror-div';
    mirror.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow: hidden;
      z-index: -1000;
    `;

    const style = getComputedStyle(textarea);
    const properties = [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
      'letterSpacing', 'textTransform', 'wordSpacing',
      'textIndent', 'whiteSpace', 'wordBreak', 'wordWrap',
      'lineHeight', 'padding', 'paddingTop', 'paddingRight',
      'paddingBottom', 'paddingLeft', 'border', 'borderWidth',
      'boxSizing', 'width'
    ];

    properties.forEach(prop => {
      mirror.style[prop as any] = style.getPropertyValue(prop.replace(/([A-Z])/g, '-$1').toLowerCase());
    });

    mirror.style.width = `${textarea.clientWidth}px`;
    const text = textarea.value.substring(0, position);

    const span = document.createElement('span');
    span.style.whiteSpace = 'pre-wrap';
    span.textContent = text;
    mirror.appendChild(span);

    const marker = document.createElement('span');
    marker.textContent = '|';
    mirror.appendChild(marker);

    document.body.appendChild(mirror);

    const coordinates = {
      top: marker.offsetTop,
      left: marker.offsetLeft,
      height: parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6
    };

    document.body.removeChild(mirror);

    return coordinates;
  }, []);

  // 编辑模式(textarea)的鼠标松开处理
  const handleTextAreaMouseUp = useCallback((e: React.MouseEvent) => {
    const textarea = e.target as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start !== end && setting) {
      const selectedText = textarea.value.substring(start, end);
      if (selectedText.trim()) {
        const coords = getCaretCoordinates(textarea, end);
        const rect = textarea.getBoundingClientRect();

        // 计算弹窗位置（相对于视口）
        // 需要减去 textarea 的 scrollTop，因为 getCaretCoordinates 返回的是相对于内容顶部的位置
        const adjustedTop = coords.top - textarea.scrollTop;

        showPopup({
          text: selectedText,
          position: {
            x: rect.left + coords.left,
            y: rect.top + adjustedTop + coords.height
          },
          sourceType: 'setting',
          sourceId: setting.id,
          contextInfo: {
            settingName: setting.name
          }
        });
      }
    } else {
      hidePopup();
    }
  }, [setting, getCaretCoordinates, showPopup, hidePopup]);

  // 显示模式(div)的鼠标松开处理
  const handleDisplayMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      hidePopup();
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText.trim() && setting) {
      const rect = range.getBoundingClientRect();

      // 使用选中范围的右下角（最后一个字的位置）
      // popupPositioning.ts 会自动处理偏移，让"引用"按钮对准目标
      showPopup({
        text: selectedText,
        position: {
          x: rect.right,
          y: rect.bottom
        },
        sourceType: 'setting',
        sourceId: setting.id,
        contextInfo: {
          settingName: setting.name
        }
      });
    } else {
      hidePopup();
    }
  }, [setting, showPopup, hidePopup]);

  // 渲染空状态
  if (!setting) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center p-8">
          {EMPTY_ICON}
          <h3 className="text-lg font-medium text-gray-900 mb-2">请选择一个设定</h3>
          <p className="text-sm text-gray-600">点击左侧设定列表中的任意项以查看详情</p>
        </div>
      </div>
    );
  }

  // 渲染正常状态
  return (
    <div className="h-full flex flex-col bg-white">
      {/* 顶部工具栏 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">设定详情</h2>
          <div className="flex gap-2">
            {!isEditing && !showDeleteConfirm && (
              <>
                <button
                  onClick={() => setShowGraphModal(true)}
                  className="px-3 py-1.5 text-sm bg-paper-50 hover:bg-paper-100 text-paper-700 rounded-lg transition-colors flex items-center gap-1.5"
                  title="查看知识图谱"
                >
                  {GRAPH_ICON}
                  知识图谱
                </button>
                <button
                  onClick={handleEdit}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                  title="编辑设定"
                >
                  {EDIT_ICON}
                  编辑
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors flex items-center gap-1.5"
                  title="删除设定"
                >
                  {DELETE_ICON}
                  删除
                </button>
              </>
            )}

            {isEditing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={loading || !editingContent.trim()}
                  className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center gap-1.5"
                  title="保存更改"
                >
                  {SAVE_ICON}
                  保存
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                  title="取消编辑"
                >
                  {CANCEL_ICON}
                  取消
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 设定信息 */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* 可滚动内容区域 */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* 基本信息卡片 */}
          <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">设定名称</label>
                <p className="text-sm font-medium text-gray-900">{setting.name}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">设定级别</label>
                <p className="text-sm font-medium text-paper-700">{getLevelLabel(setting.level)}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">设定类型</label>
                <p className="text-sm font-medium text-gray-900">{getTypeLabel(setting.setting_type)}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">作用范围</label>
                <p className="text-sm text-gray-700">{formatScope(setting.level, setting.scope)}</p>
              </div>
            </div>
          </div>

          {/* Markdown内容区域 - 占据剩余空间 */}
          <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 400px)' }}>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              设定内容 (Markdown)
            </label>
            {isEditing ? (
              <textarea
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                onMouseUp={handleTextAreaMouseUp}
                className="flex-1 w-full min-h-[300px] p-4 text-sm font-mono border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none bg-white transition-all"
                placeholder="# 标题&#10;&#10;在此输入设定的详细内容..."
                spellCheck={false}
              />
            ) : (
              <div
                onMouseUp={handleDisplayMouseUp}
                className={`flex-1 w-full min-h-[300px] p-4 text-sm font-mono border rounded-xl overflow-auto whitespace-pre-wrap break-words transition-all duration-500 ${
                  showHighlight
                    ? 'bg-amber-50 border-amber-400 shadow-lg'
                    : 'bg-white border-gray-200'
                }`}
              >
                {setting.content || '<span class="text-gray-400">暂无内容</span>'}
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              {isEditing
                ? '使用 Markdown 格式编写设定内容，支持标题、列表、粗体等格式'
                : '支持 Markdown 格式显示'}
            </div>
          </div>
        </div>

        {/* 时间信息 - 固定在底部 */}
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">创建时间</label>
              <p className="text-xs text-gray-700">{formatDateTime(setting.created_at)}</p>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">更新时间</label>
              <p className="text-xs text-gray-700">{formatDateTime(setting.updated_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 删除确认对话框 */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white flex items-center justify-center p-4 z-10">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除设定</h3>
            <p className="text-sm text-gray-600 mb-4">
              您确定要删除设定「{setting.name}」吗？此操作无法撤销。
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                disabled={loading}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 disabled:bg-gray-200 disabled:cursor-not-allowed text-gray-700 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {loading && LOADING_SPINNER}
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-200">
          <div className="flex items-center gap-2 text-red-600">
            {ERROR_ICON}
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* 加载状态 */}
      {loading && !showDeleteConfirm && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="flex items-center gap-2">
            {LOADING_SPINNER}
            <span className="text-sm text-gray-600">处理中...</span>
          </div>
        </div>
      )}

      {/* 知识图谱模态框 */}
      {showGraphModal && (
        <KnowledgeGraphModal
          isOpen={showGraphModal}
          onClose={() => setShowGraphModal(false)}
          workspaceId={workspaceId}
          settingId={setting.id}
          settingName={setting.name}
        />
      )}
    </div>
  );
};

export default SettingDetail;